import { FormProvider, useFieldArray } from "react-hook-form";
import { Button } from "primereact/button";
import {
  IInterviewQuestionFormProps,
  DIFFICULTY_LEVELS,
} from "./InterviewQuestion.model";
import { FormRow } from "../../components/FormRow";
import { CustomInput } from "../../components/CustomInput";
import { CustomSelect } from "../../components/CustomSelect";
import { TopicService } from "../../service/Topic/topic.service";
import { useEffect, useState } from "react";
import { Checkbox } from "primereact/checkbox";
import { STATUS_OPTIONS } from "../../library/utilities/constants/constant";

const InterviewQuestionForm = ({
  methods,
  onSubmit,
  isEdit,
}: IInterviewQuestionFormProps) => {
  const [topicOptions, setTopicOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [correctAnswerError, setCorrectAnswerError] = useState<string>("");

  const { control, watch, setValue } = methods;
  const { fields } = useFieldArray({
    control,
    name: "choices",
  });

  const loadTopics = async () => {
    const res = await TopicService.getTopics(true);
    const sortedData = [...res.data].sort((a, b) => a.id - b.id);
    const options = sortedData.map((topic) => ({
      label: topic.name,
      value: topic.id,
    }));
    setTopicOptions(options);
  };

  useEffect(() => {
    loadTopics();
  }, []);

  const handleCorrectChange = (index: number) => {
    // Set all choices to false first
    fields.forEach((_, i) => {
      setValue(`choices.${i}.is_correct`, false);
    });
    // Set the selected one to true
    setValue(`choices.${index}.is_correct`, true);
    // Clear error when user selects an answer
    setCorrectAnswerError("");
  };

  const handleFormSubmit = (data: any) => {
    // Check if at least one correct answer is selected
    const hasCorrectAnswer = data.choices?.some(
      (choice: any) => choice.is_correct,
    );

    if (!hasCorrectAnswer) {
      setCorrectAnswerError("Please select at least one correct answer");
      return;
    }

    setCorrectAnswerError("");
    onSubmit(data);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleFormSubmit)}
        className="p-fluid">
        <FormRow label="Question Text *" name="question_text">
          <CustomInput
            name="question_text"
            rules={{
              required: "Question text is required",
              minLength: {
                value: 5,
                message: "Question text must be at least 5 characters",
              },
            }}
          />
        </FormRow>

        <FormRow label="Topic *" name="topic">
          <CustomSelect
            name="topic"
            options={topicOptions}
            placeholder="Select topic"
            rules={{
              required: "Topic is required",
              validate: (value: number) =>
                value > 0 || "Please select a valid topic",
            }}
          />
        </FormRow>

        <FormRow label="Difficulty Level *" name="difficulty_level">
          <CustomSelect
            name="difficulty_level"
            options={DIFFICULTY_LEVELS}
            placeholder="Select difficulty"
            rules={{ required: "Difficulty level is required" }}
          />
        </FormRow>

        <FormRow label="Status *" name="is_active">
          <CustomSelect
            name="is_active"
            options={STATUS_OPTIONS}
            placeholder="Select status"
          />
        </FormRow>

        {/* Choices Section */}
        <div className="mb-4">
          <h4>Choices (Select one correct answer)</h4>
          {fields.map((field, index) => {
            const isCorrect = watch(`choices.${index}.is_correct`);
            return (
              <div
                key={field.id}
                className="mb-3 p-3 border-1 surface-border border-round">
                <div className="flex align-items-center gap-3">
                  <div className="flex-1">
                    <FormRow
                      label={`Choice ${index + 1} *`}
                      name={`choices.${index}.choice_text`}>
                      <CustomInput
                        name={`choices.${index}.choice_text`}
                        placeholder={`Enter choice ${index + 1}`}
                        rules={{
                          required: `Choice ${index + 1} is required`,
                          minLength: {
                            value: 1,
                            message: `Choice ${index + 1} cannot be empty`,
                          },
                        }}
                      />
                    </FormRow>
                  </div>
                  <div className="flex align-items-center gap-2 mt-2">
                    <Checkbox
                      inputId={`correct-${index}`}
                      checked={isCorrect}
                      onChange={() => handleCorrectChange(index)}
                    />
                    <label
                      htmlFor={`correct-${index}`}
                      className="cursor-pointer">
                      Correct Answer
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
          {correctAnswerError && (
            <small className="p-error block mt-2">{correctAnswerError}</small>
          )}
        </div>

        {/* Submit */}
        <div className="mt-4 flex justify-content-end">
          <Button
            label={isEdit ? "Update Question" : "Create Question"}
            type="submit"
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default InterviewQuestionForm;
